import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Users, 
  LogOut, 
  Menu, 
  X,
  Settings, 
  Building,
  ChevronDown,
  User,
  Lock,
  Smartphone,
  Globe,
  Check 
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import PasswordChangeModal from '../auth/PasswordChangeModal';
import InstallModal from '../common/InstallModal';
import { checkInstallable, installApp } from '../../utils/installUtils';

const getSetupItems = (navigate, t) => [
  {
    label: 'companySetup',
    onClick: () => navigate('/company-setup'),
    icon: <Building className="h-4 w-4" />,
    text: t('header.setup.companySetup')
  },
  {
    label: 'userManagement',
    onClick: () => navigate('/user-management'),
    icon: <Users className="h-4 w-4" />,
    text: t('header.setup.userManagement')
  }
];

const Header = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openMobileSubmenu, setOpenMobileSubmenu] = useState(null);
  const [isSetupDropdownOpen, setIsSetupDropdownOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isInstallModalOpen, setIsInstallModalOpen] = useState(false);
  const [installState, setInstallState] = useState({ isInstallable: false, isIOS: false });

  const setupRef = useRef(null);
  const userMenuRef = useRef(null);

  const setupItems = getSetupItems(navigate, t);

  const languages = [
    { code: 'en', name: t('header.languages.en') },
    { code: 'cs', name: t('header.languages.cs') }
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (setupRef.current && !setupRef.current.contains(event.target)) {
        setIsSetupDropdownOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const checkInstallState = () => {
      setInstallState(checkInstallable());
    };
    
    checkInstallState();
    window.addEventListener('appinstalled', checkInstallState);
    
    return () => window.removeEventListener('appinstalled', checkInstallState);
  }, []);

  const handleLanguageChange = async (languageCode) => {
    try {
      await i18n.changeLanguage(languageCode);
      setIsUserMenuOpen(false);
      setIsMobileMenuOpen(false);
    } catch (error) {
      console.error('Language change error:', error);
    }
  };

  const toggleMobileSubmenu = (submenu) => {
    setOpenMobileSubmenu(openMobileSubmenu === submenu ? null : submenu);
  };

  const handleAddToHomeScreen = () => {
    setIsUserMenuOpen(false);
    setIsInstallModalOpen(true);
  };

  return (
    <nav className="bg-white shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo/Home link */}
          {/* Logo/Home link section */}
          <div className="flex items-center">
          <div className="flex items-center gap-8">
            {/* Developer section - now clickable with vertical layout */}
            <div 
              className="flex flex-col items-center cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => navigate('/dashboard')}
            >
              <img
                src="/images/developer-logo.png"
                alt="Developer Logo"
                className="h-8 w-auto"
              />
              <span className="text-xs text-gray-500 mt-1">
                ProjectManagement
              </span>
            </div>

            {/* Vertical divider */}
            <div className="h-10 w-px bg-gray-300"></div>

            {/* Customer section - now clickable */}
            <div 
              className="flex items-center cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => navigate('/dashboard')}
            >
              <img
                src="/images/customer-logo.png"
                alt="Customer Logo"
                className="h-8 w-auto"
              />
            </div>
          </div>
        </div>

          {/* Desktop Navigation */}
          <div className="hidden sm:flex sm:items-center sm:space-x-4">
            <div className="flex items-center space-x-4">
              {/* Setup Dropdown */}
              {isAdmin() && (
                <div ref={setupRef} className="relative">
                  <button
                    onClick={() => {
                      setIsSetupDropdownOpen(!isSetupDropdownOpen);
                      setIsUserMenuOpen(false);
                    }}
                    className="flex items-center px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                  >
                    <Settings className="h-5 w-5 mr-2" />
                    {t('header.setup.title')}
                    <ChevronDown className={`h-4 w-4 ml-1 transition-transform ${isSetupDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {isSetupDropdownOpen && (
                    <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                      {setupItems.map((item, index) => (
                        <button
                          key={index}
                          onClick={item.onClick}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          {item.icon}
                          <span className="ml-2">{item.text}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* User Menu */}
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => {
                    setIsUserMenuOpen(!isUserMenuOpen);
                    setIsSetupDropdownOpen(false);
                  }}
                  className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                >
                  <User className="h-5 w-5" />
                  <span>{user.name}</span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                    {/* Language Selection */}
                    <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {t('header.profile.language')}
                    </div>
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                        className={`flex items-center w-full px-4 py-2 text-sm ${
                          i18n.language === lang.code
                            ? 'text-blue-600 bg-blue-50'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <Globe className="h-4 w-4 mr-2" />
                        {lang.name}
                        {i18n.language === lang.code && (
                          <Check className="h-4 w-4 ml-auto" />
                        )}
                      </button>
                    ))}

                    <div className="border-t my-1"></div>

                    <button
                      onClick={() => {
                        setIsPasswordModalOpen(true);
                        setIsUserMenuOpen(false);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <Lock className="h-4 w-4 mr-2" />
                      {t('header.profile.changePassword')}
                    </button>
                    
                    {installState.isInstallable && (
                      <button
                        onClick={handleAddToHomeScreen}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <Smartphone className="h-4 w-4 mr-2" />
                        {t('header.profile.addToHomeScreen')}
                      </button>
                    )}

                    <div className="border-t my-1"></div>

                    <button
                      onClick={logout}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      {t('header.profile.logout')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="flex items-center sm:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100"
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="sm:hidden bg-white border-t">
          {/* User Section */}
          <div className="px-4 py-3 border-b">
            <div className="text-sm font-medium text-gray-800">
              <button
                onClick={() => toggleMobileSubmenu('user')}
                className="flex items-center justify-between w-full"
              >
                <div className="flex items-center">
                  <User className="h-5 w-5 mr-2" />
                  <span>{user.name}</span>
                </div>
                <ChevronDown className={`h-4 w-4 transition-transform ${openMobileSubmenu === 'user' ? 'rotate-180' : ''}`} />
              </button>
            </div>

            {openMobileSubmenu === 'user' && (
              <div className="mt-2 space-y-2 pl-7">
                {/* Language Selection */}
                <div className="py-2">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    {t('header.profile.language')}
                  </div>
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      className={`flex items-center w-full py-2 text-sm ${
                        i18n.language === lang.code
                          ? 'text-blue-600 bg-blue-50'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <Globe className="h-4 w-4 mr-2" />
                      {lang.name}
                      {i18n.language === lang.code && (
                        <Check className="h-4 w-4 ml-auto" />
                      )}
                    </button>
                  ))}
                </div>

                <div className="border-t my-1"></div>

                <button
                  onClick={() => {
                    setIsPasswordModalOpen(true);
                    setIsMobileMenuOpen(false);
                  }}
                  className="flex items-center w-full py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <Lock className="h-4 w-4 mr-2" />
                  {t('header.profile.changePassword')}
                </button>

                {installState.isInstallable && (
                  <button
                    onClick={() => {
                      handleAddToHomeScreen();
                      setIsMobileMenuOpen(false);
                    }}
                    className="flex items-center w-full py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <Smartphone className="h-4 w-4 mr-2" />
                    {t('header.profile.addToHomeScreen')}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Setup Section */}
          {isAdmin() && (
            <div>
              <button
                onClick={() => toggleMobileSubmenu('setup')}
                className="flex items-center justify-between w-full px-4 py-3 text-gray-700 hover:bg-gray-100"
              >
                <div className="flex items-center">
                  <Settings className="h-5 w-5 mr-3" />
                  <span>{t('header.setup.title')}</span>
                </div>
                <ChevronDown className={`h-4 w-4 transition-transform ${openMobileSubmenu === 'setup' ? 'rotate-180' : ''}`} />
              </button>

              {openMobileSubmenu === 'setup' && (
                <div className="bg-gray-50 py-2">
                  {setupItems.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        item.onClick();
                        setIsMobileMenuOpen(false);
                      }}
                      className="flex items-center w-full px-7 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      {item.icon}
                      <span className="ml-2">{item.text}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Logout */}
          <div
            onClick={logout}
            className="flex items-center px-4 py-3 text-gray-700 hover:bg-gray-100 cursor-pointer border-t"
          >
            <LogOut className="h-5 w-5 mr-3" />
            <span>{t('header.profile.logout')}</span>
          </div>
        </div>
      )}

      {/* Modals */}
      {isPasswordModalOpen && (
        <PasswordChangeModal
          isOpen={isPasswordModalOpen}
          onClose={() => setIsPasswordModalOpen(false)}
        />
      )}

      {/* Install Modal */}
      <InstallModal
        isOpen={isInstallModalOpen}
        onClose={() => setIsInstallModalOpen(false)}
        isIOS={installState.isIOS}
      />
    </nav>
  );
};

export default Header;